import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import EvelynIcon from 'components/atoms/icons/evelyn';
import useUser from 'utils/useUser';
import TrashDeleteIcon from 'components/atoms/icons/trashIcon';
import useAlert from 'infrastructure/hooks/utils/use-alert';

import s from './styles.module.scss';

import type { CallComment } from 'infrastructure/classes/comments/call-comment';
import type { Comment } from 'infrastructure/classes/comments/comment';

interface IGeneralCommentsListMessageItemProps {
  data: Comment | CallComment;
  onDeleteComment?: () => Promise<void>;
}

const GeneralCommentsListMessageItem: React.FC<
  IGeneralCommentsListMessageItemProps
> = (props) => {
  const { data, onDeleteComment } = props;

  const { t } = useTranslation();
  const { isAdmin } = useUser();
  const { Alert, showAlert } = useAlert();

  const itemClassNames = classNames(s.item, s.message, {
    [s.mock]: data.createdBy === 'mock',
  });

  const handleDelete = async () => {
    const message = `${t('labels.commentDeleteConfirm')} ${t(
      'labels.commentDeleteConfirmInteractiveTime',
    )}`;
    const alertAnswer = await showAlert({
      title: t('labels.commentDeleteTitle'),
      messageTitle: message,
      successBtnLabel: t('controls.yes'),
      rejectBtnLabel: t('controls.no'),
    });

    if (alertAnswer) {
      await onDeleteComment?.();
    }
  };

  return (
    <div className={itemClassNames} data-cy="general-comment-item">
      <div className={s.head} data-cy="general-comment-header">
        <p className={s.title}>{t(`labels.comment`)}</p>
        <div className={s.head} data-cy="general-comment-header">
          <EvelynIcon />
          {isAdmin && (
            <div className={s['top-right-actions']}>
              <button
                className={s['delete-button']}
                onClick={handleDelete}
                aria-label="Delete comment"
              >
                <TrashDeleteIcon />
              </button>
            </div>
          )}
          {Alert}
        </div>
      </div>
      <p className={s.text} data-cy="general-comment-text">
        {data.message}
      </p>

      <p className={s.footer} data-cy="general-comment-footer">
        <span>
          {`${t('labels.electronicallySignedBy')}: ${data.createdFullName}`}
        </span>
        <span>
          {DateFormatter({
            date: data.createdAt,
            format: DateFormats['MM-dd-yyyy | h:mm a'],
          })}
        </span>
      </p>
    </div>
  );
};

export default GeneralCommentsListMessageItem;
